import { useParams, Navigate, Link } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import PublicNav from '../../components/PublicNav';
import PublicFooter from '../../components/PublicFooter';
import { ff, mono } from '../../styles/fonts';
import { usePageMeta } from '../../hooks/usePageMeta';
import { useSchema } from '../../hooks/useSchema';
import { blogPosts } from '../../data/blogPosts';

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  // Get 2 related posts (excluding current)
  const relatedPosts = blogPosts.filter(p => p.slug !== slug).slice(0, 2);

  usePageMeta(
    `${post.title} | Learnex Blog`,
    post.excerpt,
    `/blog/${post.slug}`,
    'article'
  );

  useSchema({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt,
        author: {
          '@type': 'Person',
          name: post.author,
          description: post.authorBio,
          jobTitle: post.authorRole,
          url: 'https://learnex.cc/about',
        },
        datePublished: post.publishedAt ?? post.date,
        dateModified: post.modifiedAt ?? post.date,
        url: `https://learnex.cc/blog/${post.slug}`,
        image: post.ogImage ?? 'https://learnex.cc/assets/og-image.png',
        publisher: {
          '@type': 'Organization',
          name: 'Learnex',
          logo: {
            '@type': 'ImageObject',
            url: 'https://learnex.cc/logo/logo-icon-tomato.svg',
          },
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://learnex.cc/' },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://learnex.cc/blog' },
          { '@type': 'ListItem', position: 3, name: post.title, item: `https://learnex.cc/blog/${post.slug}` },
        ],
      },
    ],
  });

  const handleShare = (e: React.MouseEvent) => {
    e.preventDefault();
    navigator.clipboard.writeText(window.location.href);
    alert('Link copied to clipboard!');
  };

  return (
    <div className="bg-[#FBF7F1] text-on-surface antialiased min-h-screen flex flex-col noise-bg">
      <motion.div
        className="fixed top-0 left-0 right-0 h-1.5 bg-primary-container origin-left z-50 shadow-sm"
        style={{ scaleX }}
      />
      <PublicNav activePath={`/blog/${post.slug}`} />
      
      {/* Editorial Header */}
      <header className="w-full pt-16 md:pt-24 pb-12 px-base md:px-gutter max-w-[900px] mx-auto text-center">
        <Link to="/blog" className="inline-flex items-center gap-2 text-on-surface-variant hover:text-primary-container font-medium text-sm mb-10 transition-colors">
          <span className="material-symbols-outlined text-[18px]" aria-hidden="true">arrow_back</span>
          Back to Journal
        </Link>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex gap-2 flex-wrap mb-8 justify-center">
          {post.tags.map(tag => (
            <span key={tag} className="text-xs font-bold tracking-wider text-primary-container bg-primary-container/10 px-3 py-1.5 rounded-full" style={mono}>
              {tag.toUpperCase()}
            </span>
          ))}
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl md:text-6xl lg:text-[4.5rem] font-bold text-on-surface mb-8 leading-[1.1] tracking-tight" style={ff}>
          {post.title}
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="text-xl md:text-2xl text-on-surface-variant max-w-[700px] mx-auto mb-10 leading-relaxed font-light">
          {post.excerpt}
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="flex items-center justify-center gap-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#b6e5d8] to-primary-container flex items-center justify-center text-white font-bold text-xl shadow-md" style={ff}>
            {post.author.charAt(0)}
          </div>
          <div className="flex flex-col text-left">
            <span className="font-bold text-on-surface text-lg">{post.author}</span>
            <span className="text-sm text-on-surface-variant" style={mono}>
              {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
        </motion.div>
      </header>
      
      <main className="flex-1 w-full max-w-[1200px] mx-auto px-base md:px-gutter pb-24 relative flex flex-col lg:flex-row gap-12 items-start">
        
        {/* Sticky Sharing Sidebar (Desktop) */}
        <aside className="hidden lg:flex flex-col gap-3 sticky top-32 w-16 shrink-0 items-center">
          <div className="w-px h-12 bg-outline-variant/30 mb-2"></div>
          
          {/* LinkedIn */}
          <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://learnex.cc/blog/${post.slug}`)}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white border border-outline-variant/20 flex items-center justify-center text-on-surface-variant hover:text-[#0A66C2] hover:border-[#0A66C2] hover:shadow-md transition-all group" title="Share on LinkedIn">
             <span className="font-bold font-sans text-[16px] group-hover:scale-110 transition-transform">in</span>
          </a>
          
          {/* Twitter / X */}
          <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(`https://learnex.cc/blog/${post.slug}`)}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white border border-outline-variant/20 flex items-center justify-center text-on-surface-variant hover:text-[#1DA1F2] hover:border-[#1DA1F2] hover:shadow-md transition-all group" title="Share on X">
             <span className="font-bold font-sans text-[16px] group-hover:scale-110 transition-transform">𝕏</span>
          </a>

          {/* Facebook */}
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://learnex.cc/blog/${post.slug}`)}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white border border-outline-variant/20 flex items-center justify-center text-on-surface-variant hover:text-[#1877F2] hover:border-[#1877F2] hover:shadow-md transition-all group" title="Share on Facebook">
             <span className="font-bold font-serif text-[16px] group-hover:scale-110 transition-transform">f</span>
          </a>

          {/* Copy Link */}
          <button onClick={handleShare} className="w-10 h-10 rounded-full bg-white border border-outline-variant/20 flex items-center justify-center text-on-surface-variant hover:text-primary-container hover:border-primary-container hover:shadow-md transition-all group" title="Copy Link">
             <span className="material-symbols-outlined text-[18px] group-hover:scale-110 transition-transform" aria-hidden="true">link</span>
          </button>
          
          <div className="w-px h-12 bg-outline-variant/30 mt-2"></div>
        </aside>

        {/* Article Content */}
        <motion.article 
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.5 }}
          className="prose prose-lg md:prose-xl prose-slate max-w-[720px] mx-auto text-on-surface-variant 
                     prose-h2:font-bold prose-h2:text-on-surface prose-h2:font-['Fraunces'] prose-h2:text-4xl prose-h2:mt-16 prose-h2:mb-6 prose-h2:leading-tight
                     prose-h3:font-bold prose-h3:text-on-surface prose-h3:text-2xl prose-h3:mt-10 prose-h3:mb-4
                     prose-p:leading-[1.8] prose-p:mb-8 prose-p:text-[1.125rem] prose-p:md:text-[1.25rem]
                     prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-8
                     prose-li:mb-3 prose-li:leading-[1.8]
                     prose-blockquote:border-l-4 prose-blockquote:border-primary-container prose-blockquote:bg-primary-container/5 prose-blockquote:p-6 prose-blockquote:rounded-r-2xl prose-blockquote:font-['Fraunces'] prose-blockquote:text-2xl prose-blockquote:text-on-surface prose-blockquote:italic
                     prose-a:text-primary-container prose-a:font-semibold prose-a:no-underline hover:prose-a:underline hover:prose-a:decoration-2 hover:prose-a:underline-offset-4
                     prose-strong:text-on-surface prose-strong:font-bold"
          dangerouslySetInnerHTML={{ __html: post.content }} 
        />
        
        {/* Empty div for right side balance on desktop */}
        <div className="hidden lg:block w-16 shrink-0"></div>
      </main>

      {/* Author & Read Next Section */}
      <section className="bg-surface-white border-t border-outline-variant/10 py-24">
        <div className="max-w-[720px] mx-auto px-base">
          {/* Author Bio */}
          <div className="bg-[#FBF7F1] rounded-[2rem] p-8 flex flex-col sm:flex-row gap-6 items-center sm:items-center shadow-inner border border-outline-variant/10 mb-20 justify-center sm:justify-start">
            {post.authorImage ? (
              <img src={post.authorImage} alt={post.author} className="w-20 h-20 shrink-0 rounded-full object-cover shadow-md" />
            ) : (
              <div className="w-20 h-20 shrink-0 rounded-full bg-gradient-to-br from-[#b6e5d8] to-primary-container flex items-center justify-center text-white font-bold text-3xl shadow-md" style={ff}>
                {post.author.charAt(0)}
              </div>
            )}
            <div className="text-center sm:text-left">
              <p className="text-xs font-bold text-primary-container mb-1 tracking-wider" style={mono}>WRITTEN BY</p>
              <h3 className="text-2xl font-bold text-on-surface mb-1" style={ff}>{post.author}</h3>
              {post.authorRole && (
                <p className="text-on-surface-variant font-medium text-base">
                  {post.authorRole}
                </p>
              )}
            </div>
          </div>

          {/* Related Articles */}
          <div>
            <h3 className="text-3xl font-bold text-on-surface mb-8 text-center" style={ff}>Read Next</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {relatedPosts.map(related => (
                <Link key={related.slug} to={`/blog/${related.slug}`} className="group bg-white border border-outline-variant/10 rounded-3xl p-6 shadow-sm hover:shadow-xl transition-all hover:-translate-y-1 flex flex-col h-full">
                   <div className="flex gap-2 flex-wrap mb-4">
                      {related.tags.slice(0, 2).map(tag => (
                        <span key={tag} className="text-[10px] font-bold tracking-wider text-primary-container bg-primary-container/10 px-2 py-1 rounded" style={mono}>
                          {tag.toUpperCase()}
                        </span>
                      ))}
                    </div>
                    <h4 className="text-xl font-bold text-on-surface mb-3 leading-tight group-hover:text-primary-container transition-colors" style={ff}>
                      {related.title}
                    </h4>
                    <p className="text-sm text-on-surface-variant line-clamp-2 mt-auto">
                      {related.excerpt}
                    </p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
